// Common
import { ColumnDef } from '@tanstack/table-core';
import { Chip, CircularProgress } from '@mui/material';

// Types
import { ExportJob, ExportStatus } from '../../types/Exports';

// Components
import ExportRowActions from '../../components/pages/Downloads/ExportRowActions';
import { dateHeader, formatCellDate } from './timeModeCell';

export interface ExportActionHandlers {
  onDownload: (job: ExportJob) => void;
  onEmail: (job: ExportJob) => void;
  onDelete: (job: ExportJob) => void;
}

const formatBytes = (bytes?: number | null): string => {
  if (!bytes || bytes <= 0) return '\u2014';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

const renderStatus = (job: ExportJob): JSX.Element => {
  if (job.isFailed || job.status === ExportStatus.Failed) {
    return <Chip size='small' variant='outlined' color='error' label='Failed' />;
  }
  if (job.isReady || job.status === ExportStatus.Completed) {
    return <Chip size='small' variant='outlined' color='success' label='Ready' />;
  }
  if (job.isProcessing || job.status === ExportStatus.Processing) {
    return <Chip size='small' variant='outlined' color='info' icon={<CircularProgress size={12} thickness={6} />} label='Processing' />;
  }
  return <Chip size='small' variant='outlined' color='warning' label='Pending' />;
};

export const makeExportListColumn = (handlers: ExportActionHandlers): ColumnDef<ExportJob>[] => {
  return [
    {
      header: 'File',
      accessorKey: 'fileName',
      enableSorting: true,
      enableColumnFilter: true,
      size: 320,
      cell: ({ row }) =>
        row.original.fileName ? (
          <span style={{ wordBreak: 'break-all' }}>{row.original.fileName}</span>
        ) : (
          <span style={{ color: '#9e9e9e' }}>{`${row.original.entityType} export`}</span>
        ),
    },
    {
      header: 'Entity',
      accessorKey: 'entityType',
      enableSorting: true,
      enableColumnFilter: true,
      size: 130,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      enableSorting: true,
      enableColumnFilter: false,
      size: 130,
      cell: ({ row }) => renderStatus(row.original),
    },
    {
      header: dateHeader('Created'),
      accessorKey: 'createdDateTime',
      enableSorting: true,
      enableColumnFilter: false,
      size: 160,
      cell: ({ row, table }) => formatCellDate(table, row.original.createdDateTime),
    },
    {
      header: dateHeader('Completed'),
      accessorKey: 'completedDateTime',
      enableSorting: true,
      enableColumnFilter: false,
      size: 160,
      cell: ({ row, table }) => formatCellDate(table, row.original.completedDateTime),
    },
    {
      header: 'Size',
      accessorKey: 'fileSize',
      enableSorting: true,
      enableColumnFilter: false,
      size: 100,
      cell: ({ row }) => formatBytes(row.original.fileSize),
    },
    {
      header: 'Actions',
      enableSorting: false,
      enableColumnFilter: false,
      size: 150,
      cell: ({ row }) => (
        <ExportRowActions job={row.original} onDownload={handlers.onDownload} onEmail={handlers.onEmail} onDelete={handlers.onDelete} />
      ),
    },
  ];
};
